import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { DoorOpen, LogOut, Shield, Trophy, User, Users, WifiOff } from 'lucide-react';
import { useAuth } from '@/store/auth';
import { useGame } from '@/store/game';
import { Avatar } from './Avatar';
import { ErrorBoundary } from './ErrorBoundary';
import { ConsentGate } from './ConsentGate';

export function Layout() {
  const { user, logout } = useAuth();
  const { lobby, connection } = useGame();
  const location = useLocation();
  const inRoomPage = lobby && location.pathname === `/room/${lobby.roomId}`;

  const nav = [
    { to: '/', label: 'Комнаты', icon: Users, end: true },
    { to: '/rating', label: 'Рейтинг', icon: Trophy, end: false },
    { to: '/profile', label: 'Профиль', icon: User, end: false },
    ...(user?.role === 'ADMIN' ? [{ to: '/admin', label: 'Админка', icon: Shield, end: false }] : []),
  ];

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-ink-950/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-2 px-4">
          <Link to="/" className="mr-2 flex items-center gap-2 font-display text-lg tracking-wide">
            <span className="text-xl">🎩</span>
            <span className="hidden sm:inline">Мафия</span>
          </Link>
          <nav className="flex flex-1 items-center gap-1">
            {nav.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm transition',
                    isActive ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white',
                  )
                }
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </NavLink>
            ))}
          </nav>
          {connection === 'offline' && (
            <span className="flex items-center gap-1 text-xs text-gold-300" title="Нет соединения, переподключаемся…">
              <WifiOff className="h-4 w-4" />
              <span className="hidden md:inline">Переподключение…</span>
            </span>
          )}
          {user && (
            <div className="flex items-center gap-2">
              <Link to="/profile" className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-white/5">
                <Avatar id={user.avatarId} name={user.nickname} size={28} />
                <span className="hidden max-w-[10rem] truncate text-sm md:inline">{user.nickname}</span>
              </Link>
              <button
                onClick={() => logout()}
                className="rounded-lg p-2 text-zinc-500 hover:bg-white/5 hover:text-white"
                title="Выйти"
                aria-label="Выйти"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        {lobby && !inRoomPage && (
          <Link
            to={`/room/${lobby.roomId}`}
            className="flex items-center justify-center gap-2 bg-gold-400/10 px-4 py-2 text-sm text-gold-300 hover:bg-gold-400/15"
          >
            <DoorOpen className="h-4 w-4" />
            {lobby.gameId ? 'Идёт ваша игра' : 'Вы в комнате'} «{lobby.name}» — вернуться
          </Link>
        )}
      </header>
      <main className="flex-1">
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
      <footer className="border-t border-white/5 py-4 text-center text-xs text-zinc-500">
        <Link to="/privacy" className="hover:text-white">
          Политика обработки персональных данных
        </Link>
      </footer>
      <ConsentGate />
    </div>
  );
}
